import { useEffect, useMemo, useState } from 'react';
import { buildAdditionWorkedExample } from '../game/math/additionFeedback';
import { buildSubtractionWorkedExample } from '../game/math/subtractionFeedback';
import { buildMultiplicationWorkedExample } from '../game/math/multiplicationFeedback';
import { buildDivisionWorkedExample } from '../game/math/divisionFeedback';
import { SUMA_MISSIONS } from '../game/planets/additionMissions';
import { RESTA_MISSIONS } from '../game/planets/subtractionMissions';
import { MULTIPLICACION_MISSIONS } from '../game/planets/multiplicationMissions';
import { DIVISION_MISSIONS } from '../game/planets/divisionMissions';
import { COSMIC_VALUE_MESSAGES, COSMIC_VALUE_ORDER, getNextCosmicValueMessage } from '../game/data/cosmicValueMessages';
import {
  getPracticeImpulseMessages,
  PRACTICE_IMPULSE_TRIGGERS,
  PRACTICE_IMPULSE_TRIGGER_LABELS,
} from '../game/data/cosmicPracticeImpulseMessages';

const OPERATION_OPTIONS = [
  ['suma', 'Suma'],
  ['resta', 'Resta'],
  ['multiplicacion', 'Multiplicación'],
  ['division', 'División'],
];

const PLANET_COUNTS = {
  suma: 6,
  resta: 6,
  multiplicacion: 8,
  division: 8,
};

// Solo estas combinaciones tienen actualmente un tutorial REAL. Las demás se
// muestran para probar el banco, pero nunca se sustituyen por un tutorial falso.
const TUTORIAL_PLANETS = {
  suma: new Set([1, 2, 3, 4, 5, 6]),
  resta: new Set([1, 2, 3, 4, 5, 6]),
  multiplicacion: new Set([1, 2, 3, 4, 5, 6, 7, 8]),
  division: new Set([1, 2, 3, 4, 5, 6, 7, 8]),
};

const add = (a, b) => ({ a, b, answer: a + b, symbol: '+' });
const subtract = (a, b) => ({ a, b, answer: a - b, symbol: '−' });
const multiply = (a, b) => ({ a, b, answer: a * b, symbol: '×' });
const divide = (a, b) => ({
  a,
  b,
  answer: Math.floor(a / b),
  remainder: a % b,
  symbol: '÷',
});

const testCase = (id, label, problem) => ({ id, label, problem });

// Casos locales de DEV: no forman parte de los bancos reales ni se guardan.
const TEST_CASES = {
  suma: {
    1: [testCase('normal', 'Normal', add(5, 6)), testCase('zero', 'Con cero', add(7, 0))],
    2: [testCase('normal', 'Normal', add(54, 21)), testCase('zero', 'Con cero', add(40, 20))],
    3: [testCase('regroup', 'Con reagrupación', add(48, 27)), testCase('multi-regroup', 'Reagrupación múltiple', add(68, 67))],
    4: [testCase('normal', 'Normal', add(654, 321)), testCase('zero', 'Con cero', add(504, 201))],
    5: [testCase('regroup', 'Con reagrupación', add(487, 368)), testCase('multi-regroup', 'Reagrupación múltiple', add(688, 677))],
    6: [testCase('normal', 'Normal', add(54, 21)), testCase('regroup', 'Con reagrupación', add(487, 368)), testCase('large', 'Caso grande / límite', add(786, 249)), testCase('zero', 'Con cero', add(7, 0))],
  },
  resta: {
    1: [testCase('normal', 'Normal', subtract(8, 3)), testCase('zero', 'Con cero', subtract(7, 0))],
    2: [testCase('normal', 'Normal', subtract(54, 21)), testCase('zero', 'Con cero', subtract(40, 20))],
    3: [testCase('regroup', 'Con reagrupación', subtract(42, 17))],
    4: [testCase('normal', 'Normal', subtract(654, 321)), testCase('zero', 'Con cero', subtract(504, 201))],
    5: [testCase('regroup', 'Con reagrupación', subtract(502, 178)), testCase('multi-regroup', 'Reagrupación múltiple', subtract(700, 286))],
    6: [testCase('normal', 'Normal', subtract(54, 21)), testCase('regroup', 'Con reagrupación', subtract(502, 178)), testCase('multi-regroup', 'Reagrupación múltiple', subtract(700, 286)), testCase('zero', 'Con cero', subtract(7, 0))],
  },
  multiplicacion: {
    1: [testCase('normal', 'Normal', multiply(4, 3)), testCase('zero', 'Con cero', multiply(3, 0))],
    2: [testCase('normal', 'Normal', multiply(23, 2)), testCase('regroup', 'Con reagrupación', multiply(27, 4)), testCase('multi-regroup', 'Reagrupación múltiple', multiply(99, 9)), testCase('zero', 'Con cero', multiply(20, 5))],
    3: [testCase('normal', 'Normal', multiply(123, 2)), testCase('regroup', 'Con reagrupación', multiply(354, 3)), testCase('multi-regroup', 'Reagrupación múltiple', multiply(999, 9)), testCase('zero', 'Con cero', multiply(203, 4))],
    4: [testCase('normal', 'Normal', multiply(1234, 2)), testCase('regroup', 'Con reagrupación', multiply(6689, 8)), testCase('multi-regroup', 'Reagrupación múltiple', multiply(9999, 9)), testCase('zero', 'Con cero', multiply(1203, 4)), testCase('large', 'Caso grande / límite', multiply(9876, 9))],
    5: [testCase('normal', 'Normal', multiply(23, 12)), testCase('partials', 'Productos parciales', multiply(23, 14)), testCase('partials-regroup', 'Productos parciales con reagrupación', multiply(99, 99)), testCase('zero', 'Con cero', multiply(20, 10)), testCase('large', 'Caso grande / límite', multiply(99, 99))],
    6: [testCase('normal', 'Normal', multiply(123, 12)), testCase('partials', 'Productos parciales', multiply(123, 24)), testCase('partials-regroup', 'Productos parciales con reagrupación', multiply(987, 98)), testCase('zero', 'Con cero', multiply(120, 10)), testCase('large', 'Caso grande / límite', multiply(999, 99))],
    7: [testCase('normal', 'Normal', multiply(1234, 12)), testCase('partials', 'Productos parciales', multiply(1234, 24)), testCase('partials-regroup', 'Productos parciales con reagrupación', multiply(9876, 98)), testCase('zero', 'Con cero', multiply(1203, 10)), testCase('large', 'Caso grande / límite', multiply(9876, 99))],
    8: [testCase('normal', 'Normal', multiply(4, 3)), testCase('partials', 'Productos parciales', multiply(23, 14)), testCase('partials-regroup', 'Productos parciales con reagrupación', multiply(9876, 98)), testCase('zero', 'Con cero', multiply(3, 0)), testCase('large', 'Caso grande / límite', multiply(9876, 99))],
  },
  division: {
    1: [testCase('normal', 'Normal', divide(63, 7)), testCase('zero', 'Con cero', divide(40, 4)), testCase('limit', 'Caso grande / límite', divide(81, 9))],
    2: [testCase('normal', 'Normal', divide(65, 4)), testCase('zero', 'Con cero', divide(70, 4)), testCase('limit', 'Caso grande / límite', divide(98, 9))],
    3: [testCase('normal', 'Normal', divide(144, 8)), testCase('zero', 'Con cero', divide(120, 6)), testCase('limit', 'Caso grande / límite', divide(992, 8))],
    4: [testCase('normal', 'Normal', divide(287, 8)), testCase('zero', 'Con cero', divide(101, 9)), testCase('limit', 'Caso grande / límite', divide(998, 9))],
    5: [testCase('normal', 'Normal', divide(1152, 8)), testCase('zero', 'Con cero', divide(8640, 8)), testCase('limit', 'Caso grande / límite', divide(9999, 9))],
    6: [testCase('normal', 'Normal', divide(1001, 9)), testCase('zero', 'Con cero', divide(4003, 9)), testCase('limit', 'Caso grande / límite', divide(9998, 9))],
    7: [testCase('normal', 'Normal', divide(1152, 24)), testCase('zero', 'Con cero', divide(1200, 12)), testCase('limit', 'Caso grande / límite', divide(9900, 99))],
    8: [testCase('normal', 'Normal', divide(84, 7)), testCase('remainder', 'Con residuo', divide(65, 4)), testCase('limit', 'Caso grande / límite', divide(9900, 99))],
  },
};

const MISSIONS_BY_OPERATION = {
  suma: SUMA_MISSIONS,
  resta: RESTA_MISSIONS,
  multiplicacion: MULTIPLICACION_MISSIONS,
  division: DIVISION_MISSIONS,
};

const END_SCENARIOS = [
  { id: 'accuracy-100', label: '100% precisión', won: true, correct: 10, wrong: 0, lives: 3, score: 5000, elapsedTime: 42, timeLeft: 48 },
  { id: 'accuracy-80', label: '80% precisión', won: true, correct: 8, wrong: 2, lives: 1, score: 4000, elapsedTime: 58, timeLeft: 32 },
  { id: 'accuracy-70', label: '70% precisión', won: true, correct: 7, wrong: 3, lives: 1, score: 3500, elapsedTime: 66, timeLeft: 20 },
  { id: 'no-lives', label: 'Sin vidas', won: false, correct: 4, wrong: 3, lives: 0, score: 2000, elapsedTime: 54, timeLeft: 25 },
  { id: 'time-up', label: 'Tiempo agotado', won: false, correct: 4, wrong: 1, lives: 2, score: 2000, elapsedTime: null, timeLeft: 0 },
];

const panelStyle = {
  position: 'absolute',
  zIndex: 30,
  left: 10,
  bottom: 10,
  width: 270,
  padding: 10,
  border: '1px solid #ffd641',
  borderRadius: 10,
  background: 'rgba(4, 10, 34, .95)',
  color: '#fff',
  font: '700 12px/1.25 system-ui, sans-serif',
  boxShadow: '0 8px 22px rgba(0, 0, 0, .5)',
};

const fieldStyle = {
  width: '100%',
  marginTop: 4,
  padding: '5px 6px',
  border: '1px solid #536b9b',
  borderRadius: 6,
  background: '#101e48',
  color: '#fff',
  font: 'inherit',
};

const buttonStyle = {
  flex: 1,
  minHeight: 30,
  border: '1px solid #74ecff',
  borderRadius: 6,
  background: '#123d78',
  color: '#fff',
  font: '800 11px/1 system-ui, sans-serif',
  cursor: 'pointer',
};

function formatProblem(problem) {
  if (!problem) return '';
  const format = value => new Intl.NumberFormat('en-US').format(value);
  const result = problem.symbol === '÷' && problem.remainder
    ? ' = ' + format(problem.answer) + ' R' + format(problem.remainder)
    : ' = ?';
  return format(problem.a) + ' ' + problem.symbol + ' ' + format(problem.b) + result;
}

function buildRealTutorial(operation, planet, problem) {
  if (operation === 'suma') return buildAdditionWorkedExample(problem);
  if (operation === 'resta') return buildSubtractionWorkedExample(problem);
  if (operation === 'multiplicacion') return buildMultiplicationWorkedExample(problem, planet);
  if (operation === 'division' && planet >= 1 && planet <= 8) {
    return buildDivisionWorkedExample(problem, {
      includeVisualRepresentation: planet === 1,
      showBringArrow: planet >= 2 && planet <= 8,
      formatLargeNumbers: planet >= 5 && planet <= 8,
      explainTwoDigitDivisor: planet === 7 || (planet === 8 && problem?.b >= 10),
    });
  }
  return null;
}

// Herramienta aislada: no accede a escenas, registry, storage ni contadores.
// Solo dispara los mismos eventos de apertura que ya consume GameCanvas.
export function PracticeTutorialDevPanel() {
  const [open, setOpen] = useState(false);
  const [operation, setOperation] = useState('suma');
  const [planet, setPlanet] = useState(1);
  const [caseId, setCaseId] = useState('normal');
  const [initialStepIndex, setInitialStepIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('tutorials');
  const [finalOperation, setFinalOperation] = useState('suma');
  const [finalMissionIndex, setFinalMissionIndex] = useState(0);
  const [finalScenarioId, setFinalScenarioId] = useState('accuracy-80');
  const [capsuleCategory, setCapsuleCategory] = useState('automatic');
  const [capsuleMessageId, setCapsuleMessageId] = useState('automatic');
  const [impulseOperation, setImpulseOperation] = useState('suma');
  const [impulseTrigger, setImpulseTrigger] = useState(PRACTICE_IMPULSE_TRIGGERS.TUTORIAL_LEARNING);
  const [impulseMessageId, setImpulseMessageId] = useState('random');

  const planetCount = PLANET_COUNTS[operation];
  const planetOptions = useMemo(
    () => Array.from({ length: planetCount }, (_, index) => index + 1),
    [planetCount],
  );
  const testCases = TEST_CASES[operation][planet] || [];
  const selectedCase = testCases.find(item => item.id === caseId) || testCases[0];
  const hasRealTutorial = TUTORIAL_PLANETS[operation].has(planet);
  const realTutorial = useMemo(
    () => hasRealTutorial && selectedCase
      ? buildRealTutorial(operation, planet, selectedCase.problem)
      : null,
    [hasRealTutorial, operation, planet, selectedCase],
  );
  const steps = realTutorial?.steps || [];

  const finalMissions = MISSIONS_BY_OPERATION[finalOperation] || SUMA_MISSIONS;
  const selectedFinalMission = finalMissions[finalMissionIndex] || finalMissions[0];
  const selectedFinalScenario = END_SCENARIOS.find(item => item.id === finalScenarioId) || END_SCENARIOS[0];
  const capsuleMessagesForCategory = capsuleCategory === 'automatic'
    ? []
    : COSMIC_VALUE_MESSAGES.filter(message => message.category === capsuleCategory);
  const selectedCapsuleMessage = capsuleCategory === 'automatic'
    ? null
    : capsuleMessagesForCategory.find(message => message.id === capsuleMessageId)
      || capsuleMessagesForCategory[0]
      || null;
  const impulseMessages = getPracticeImpulseMessages(impulseOperation, impulseTrigger);
  const selectedImpulseMessage = impulseMessageId === 'random'
    ? impulseMessages[Math.floor(Math.random() * impulseMessages.length)] || null
    : impulseMessages.find(message => message.id === impulseMessageId)
      || impulseMessages[0]
      || null;

  useEffect(() => {
    const nextCase = testCases[0];
    if (!testCases.some(item => item.id === caseId)) {
      setCaseId(nextCase?.id || '');
    }
  }, [operation, planet, caseId, testCases]);

  useEffect(() => {
    setInitialStepIndex(current => Math.min(Math.max(0, current), Math.max(0, steps.length - 1)));
  }, [steps.length]);

  useEffect(() => {
    if (finalMissionIndex >= finalMissions.length) setFinalMissionIndex(0);
  }, [finalMissionIndex, finalMissions.length]);

  const selectOperation = value => {
    setOperation(value);
    setPlanet(1);
    setCaseId(TEST_CASES[value][1][0].id);
    setInitialStepIndex(0);
  };

  const selectPlanet = value => {
    const nextPlanet = Number(value);
    setPlanet(nextPlanet);
    setCaseId(TEST_CASES[operation][nextPlanet][0].id);
    setInitialStepIndex(0);
  };

  const openRealTutorial = () => {
    if (!realTutorial || typeof window === 'undefined') return;

    const sessionId = 'dev-' + operation + '-p' + planet + '-' + Date.now();
    const eventName = operation === 'suma'
      ? 'tmag:practice-addition-tutorial-open'
      : operation === 'resta'
        ? 'tmag:practice-subtraction-tutorial-open'
        : operation === 'division'
          ? 'tmag:practice-division-tutorial-open'
          : 'tmag:practice-multiplication-tutorial-open';

    // Misma entrada que usa PracticeScene tras el tercer error. El índice solo
    // existe en este payload local y los tutoriales normales siguen en Paso 1.
    window.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        sessionId,
        initialStepIndex,
        tutorial: realTutorial,
      },
    }));
    setOpen(false);
  };

  const selectCapsuleCategory = category => {
    setCapsuleCategory(category);
    const firstMessage = COSMIC_VALUE_MESSAGES.find(message => message.category === category);
    setCapsuleMessageId(firstMessage?.id || 'automatic');
  };

  const selectImpulseTrigger = trigger => {
    setImpulseTrigger(trigger);
    setImpulseMessageId('random');
  };

  const previewPracticeImpulse = () => {
    if (typeof window === 'undefined' || !selectedImpulseMessage) return;
    const planetId = impulseOperation === 'multiplicacion' ? 'mult' : impulseOperation;
    window.dispatchEvent(new CustomEvent('tmag:dev-practice-cosmic-impulse-open', {
      detail: {
        devPreview: true,
        sessionId: 'dev-practice-impulse-' + Date.now(),
        planetId,
        trigger: impulseTrigger,
        message: selectedImpulseMessage,
      },
    }));
  };

  const previewCapsule = () => {
    if (typeof window === 'undefined') return;
    const message = selectedCapsuleMessage || getNextCosmicValueMessage();
    const planetId = finalOperation === 'multiplicacion' ? 'mult' : finalOperation;
    window.dispatchEvent(new CustomEvent('tmag:dev-cosmic-capsule-open', {
      detail: {
        sessionId: 'dev-capsule-' + Date.now(),
        planetId,
        message,
      },
    }));
  };

  const previewMissionEnd = () => {
    if (typeof window === 'undefined' || !selectedFinalMission) return;
    const planetId = finalOperation === 'multiplicacion' ? 'mult' : finalOperation;
    window.dispatchEvent(new CustomEvent('tmag:dev-final-preview-start', {
      detail: {
        devPreview: true,
        planetId,
        mission: selectedFinalMission,
        scenario: selectedFinalScenario,
        capsuleMessage: selectedCapsuleMessage,
      },
    }));
    setOpen(false);
  };

  const tabStyle = active => ({
    ...buttonStyle,
    flex: 1,
    minHeight: 28,
    borderColor: active ? '#ffd641' : '#536b9b',
    background: active ? '#3a2a68' : '#101e48',
    color: active ? '#fff2a0' : '#d9eaff',
  });

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 30 }}>
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        style={{
          position: 'absolute', left: 10, bottom: 10, minWidth: 54, minHeight: 28,
          border: '1px solid #ffd641', borderRadius: 7, background: '#1c163f', color: '#ffd641',
          font: '900 11px/1 system-ui, sans-serif', pointerEvents: 'auto', cursor: 'pointer',
        }}
      >
        DEV · QA
      </button>

      {open ? (
        <section style={{ ...panelStyle, pointerEvents: 'auto' }} aria-label="Panel QA de desarrollo">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <strong style={{ color: '#ffd641', letterSpacing: '.08em' }}>DEV · QA</strong>
            <button type="button" onClick={() => setOpen(false)} style={{ ...buttonStyle, flex: '0 0 26px' }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
            <button type="button" onClick={() => setActiveTab('tutorials')} style={tabStyle(activeTab === 'tutorials')}>TUTORIALES</button>
            <button type="button" onClick={() => setActiveTab('final')} style={tabStyle(activeTab === 'final')}>FINAL</button>
            <button type="button" onClick={() => setActiveTab('impulse')} style={tabStyle(activeTab === 'impulse')}>IMPULSO</button>
          </div>

          {activeTab === 'tutorials' ? (
            <>
              <label style={{ display: 'block', marginTop: 8 }}>
                Operación
                <select value={operation} onChange={event => selectOperation(event.target.value)} style={fieldStyle}>
                  {OPERATION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Planeta
                <select value={planet} onChange={event => selectPlanet(event.target.value)} style={fieldStyle}>
                  {planetOptions.map(value => <option key={value} value={value}>Planeta {value}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Caso de prueba
                <select value={selectedCase?.id || ''} onChange={event => { setCaseId(event.target.value); setInitialStepIndex(0); }} style={fieldStyle}>
                  {testCases.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
              </label>
              <p style={{ margin: '8px 0', color: '#d9f7ff' }}>Ejercicio: <strong>{formatProblem(selectedCase?.problem)}</strong></p>
              {hasRealTutorial ? (
                <>
                  <label style={{ display: 'block', marginTop: 7 }}>
                    Paso inicial
                    <select value={initialStepIndex} onChange={event => setInitialStepIndex(Number(event.target.value))} style={fieldStyle}>
                      {steps.map((step, index) => <option key={step.id || index} value={index}>{index === steps.length - 1 ? 'Último paso' : 'Paso ' + (index + 1)}</option>)}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                    <button type="button" onClick={openRealTutorial} style={buttonStyle}>ABRIR TUTORIAL</button>
                    <button type="button" onClick={openRealTutorial} style={buttonStyle}>SIMULAR 3 ERRORES</button>
                  </div>
                </>
              ) : <p style={{ margin: '8px 0 0', color: '#ffd89d' }}>Tutorial no disponible para este planeta. DEV no crea una versión falsa.</p>}
            </>
          ) : activeTab === 'final' ? (
            <>
              <label style={{ display: 'block', marginTop: 8 }}>
                Mundo
                <select value={finalOperation} onChange={event => { setFinalOperation(event.target.value); setFinalMissionIndex(0); }} style={fieldStyle}>
                  {OPERATION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Planeta / misión
                <select value={finalMissionIndex} onChange={event => setFinalMissionIndex(Number(event.target.value))} style={fieldStyle}>
                  {finalMissions.map(mission => <option key={mission.index} value={mission.index}>{mission.name} · {mission.skill}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Escenario
                <select value={finalScenarioId} onChange={event => setFinalScenarioId(event.target.value)} style={fieldStyle}>
                  {END_SCENARIOS.map(scenario => <option key={scenario.id} value={scenario.id}>{scenario.label}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Cápsula / valor
                <select value={capsuleCategory} onChange={event => selectCapsuleCategory(event.target.value)} style={fieldStyle}>
                  <option value="automatic">Automática</option>
                  {COSMIC_VALUE_ORDER.map(category => <option key={category} value={category}>{category}</option>)}
                </select>
              </label>
              {capsuleCategory !== 'automatic' ? (
                <label style={{ display: 'block', marginTop: 7 }}>
                  Mensaje
                  <select value={selectedCapsuleMessage?.id || ''} onChange={event => setCapsuleMessageId(event.target.value)} style={fieldStyle}>
                    {capsuleMessagesForCategory.map((message, index) => (
                      <option key={message.id} value={message.id}>Mensaje {index + 1}</option>
                    ))}
                  </select>
                </label>
              ) : null}
              <p style={{ margin: '8px 0 0', color: '#9ef8ff', fontSize: 11 }}>Vista QA: no guarda progreso, puntuación, ranking ni desbloqueos.</p>
              <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                <button type="button" onClick={previewCapsule} style={buttonStyle}>VER CÁPSULA</button>
                <button type="button" onClick={previewMissionEnd} style={buttonStyle}>PROBAR FINAL COMPLETO</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: '8px 0 2px', color: '#9ef8ff', fontSize: 11 }}>
                IMPULSO CÓSMICO · vista real, sin guardar datos.
              </p>
              <label style={{ display: 'block', marginTop: 7 }}>
                Mundo
                <select value={impulseOperation} onChange={event => { setImpulseOperation(event.target.value); setImpulseMessageId('random'); }} style={fieldStyle}>
                  {OPERATION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Tipo
                <select value={impulseTrigger} onChange={event => selectImpulseTrigger(event.target.value)} style={fieldStyle}>
                  {Object.values(PRACTICE_IMPULSE_TRIGGERS).map(trigger => (
                    <option key={trigger} value={trigger}>{PRACTICE_IMPULSE_TRIGGER_LABELS[trigger]}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 7 }}>
                Mensaje
                <select value={impulseMessageId} onChange={event => setImpulseMessageId(event.target.value)} style={fieldStyle}>
                  <option value="random">Aleatorio</option>
                  {impulseMessages.map((message, index) => <option key={message.id} value={message.id}>Mensaje {index + 1}</option>)}
                </select>
              </label>
              <p style={{ margin: '8px 0 0', color: '#d9f7ff', fontSize: 11 }}>
                El botón real tarda 1 segundo en aparecer, igual que durante Práctica.
              </p>
              <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                <button type="button" onClick={previewPracticeImpulse} style={buttonStyle}>VER IMPULSO</button>
                <button type="button" onClick={previewPracticeImpulse} style={buttonStyle}>PROBAR FLUJO</button>
              </div>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}

export default PracticeTutorialDevPanel;
